import type { TimeSlot, Weekday, WeeklySchedule } from "~/types/availability";

// ── Weekday metadata ────────────────────────────────────────────────────────
// Days are ordered Sunday-first (S M T W T F S) to match the calendar grids,
// while the backend keys the weekly schedule by name. WEEKDAYS bridges the two:
// index 0 = Sunday … 6 = Saturday. `isWeekend` distinguishes Sat/Sun for the
// "weekdays & weekends differ" pattern regardless of position.

export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const WEEKDAYS: {
  index: WeekdayIndex;
  weekday: Weekday;
  label: string;
  short: string;
  char: string;
  isWeekend: boolean;
}[] = [
  {
    index: 0,
    weekday: "sunday",
    label: "Sunday",
    short: "Sun",
    char: "S",
    isWeekend: true,
  },
  {
    index: 1,
    weekday: "monday",
    label: "Monday",
    short: "Mon",
    char: "M",
    isWeekend: false,
  },
  {
    index: 2,
    weekday: "tuesday",
    label: "Tuesday",
    short: "Tue",
    char: "T",
    isWeekend: false,
  },
  {
    index: 3,
    weekday: "wednesday",
    label: "Wednesday",
    short: "Wed",
    char: "W",
    isWeekend: false,
  },
  {
    index: 4,
    weekday: "thursday",
    label: "Thursday",
    short: "Thu",
    char: "T",
    isWeekend: false,
  },
  {
    index: 5,
    weekday: "friday",
    label: "Friday",
    short: "Fri",
    char: "F",
    isWeekend: false,
  },
  {
    index: 6,
    weekday: "saturday",
    label: "Saturday",
    short: "Sat",
    char: "S",
    isWeekend: true,
  },
];

/** Weekday indices that are weekends (Sunday & Saturday). */
export const WEEKEND_INDICES: WeekdayIndex[] = [0, 6];
/** Weekday indices Monday–Friday, in order. */
export const WEEKDAY_INDICES: WeekdayIndex[] = [1, 2, 3, 4, 5];

// ── Setup state ───────────────────────────────────────────────────────────────

export type AvailabilityMode = "weekly" | "events" | "away" | "both";
export type WeeklyPattern = "same" | "weekend" | "each";

/** A time window while editing. Times are "HH:mm" 24h strings. */
export type HourSlot = { start: string; end: string };

/**
 * A single event *day* the host has opted their spot into. We snapshot the
 * fields needed to compute open windows (that day's real start/end times) so
 * the translation is pure and doesn't depend on re-fetching the events API.
 *
 * One entry per occurrence, not per event: a three-day festival is three
 * independent choices, and a host who only wants to open for the Saturday must
 * be able to say so. `startsAt`/`endsAt` are therefore the *occurrence's*
 * window, never the event's legacy top-level pair (which spans the whole run).
 *
 * `id` is the row's identity — `${eventId}:${occurrenceId}` — because the event
 * id repeats across its days and so can't distinguish them.
 *
 * No migration is needed for the older shape, where `id` was a bare event id:
 * this state is only ever built inside a wizard session. `listingStore` holds it
 * in memory with no `persist` middleware, and the edit flows construct a fresh
 * setup rather than rehydrating one, so a selection made before this shipped
 * cannot outlive the deploy that changed the meaning of the field.
 */
export type SelectedEvent = {
  id: string;
  /**
   * The parent event's id. Set by the picker, which needs it to group a run's
   * days ("Day 2 of 3"); optional because the window translation below never
   * reads it, so callers constructing a setup directly don't have to supply one.
   */
  eventId?: string;
  name: string;
  venue: string | null;
  startsAt: string; // ISO datetime
  endsAt: string; // ISO datetime
};

/** Buffer presets (minutes) offered around each event's real times. */
export const BUFFER_OPTIONS = [0, 30, 60, 90, 120, 180] as const;

export interface AvailabilitySetupState {
  mode: AvailabilityMode | null;
  // Weekly
  days: WeekdayIndex[];
  pattern: WeeklyPattern | null;
  sameHrs: HourSlot[];
  sameAllDay: boolean;
  weekdayHrs: HourSlot[];
  weekendHrs: HourSlot[];
  perDay: Record<number, HourSlot[]>;
  // Events — driven by real event times + a buffer, with optional per-date tweaks
  selectedEvents: SelectedEvent[];
  bufferBeforeMin: number;
  bufferAfterMin: number;
  /** Manual per-date overrides that replace the buffer-computed window. */
  eventDateSlots: Record<string, HourSlot[]>;
  // Custom schedule — closed by default; each opened date maps to its windows.
  customDates: Record<string, HourSlot[]>;
  // Single dates the host explicitly closed, e.g. blocking one weekly-open day.
  // Wins over the weekly baseline and any custom/event window on that date.
  blockedDates: Record<string, true>;
}

export function createInitialSetupState(): AvailabilitySetupState {
  return {
    mode: null,
    days: [...WEEKDAY_INDICES],
    // No separate "pattern" question — each open day gets its own hours in the
    // hours step (hosts add extra windows per day with "Add time").
    pattern: "each",
    sameHrs: [{ start: "08:00", end: "18:00" }],
    sameAllDay: false,
    weekdayHrs: [{ start: "08:00", end: "18:00" }],
    weekendHrs: [{ start: "10:00", end: "16:00" }],
    perDay: {},
    selectedEvents: [],
    bufferBeforeMin: 120,
    bufferAfterMin: 120,
    eventDateSlots: {},
    customDates: {},
    blockedDates: {},
  };
}

// ── Time helpers ───────────────────────────────────────────────────────────

/** "HH:mm" → minutes since midnight. */
export function timeToMinute(time: string): number {
  const [h, m] = time.split(":");
  return Number.parseInt(h ?? "0", 10) * 60 + Number.parseInt(m ?? "0", 10);
}

/** minutes since midnight → "HH:mm" (clamped to 23:59). */
export function minuteToTime(minute: number): string {
  const clamped = Math.min(Math.max(minute, 0), 1439);
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** minutes since midnight → "9 AM" / "9:30 AM" display. 1440 shows as 11:59 PM. */
export function formatMinute(minute: number): string {
  const min = minute >= 1440 ? 1439 : minute;
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0
    ? `${h12} ${ap}`
    : `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

/**
 * minutes since midnight → "9:00 AM" display, minutes always shown. 1440 shows
 * as 11:59 PM.
 *
 * Same value as `formatMinute` above, differing only in that it never elides
 * ":00". That's the convention the availability calendar authors hours in — its
 * hour dropdowns, slot summaries and the copy naming the spot's current time all
 * read the padded form, so a bare "12 PM" next to a "12:00 PM" dropdown reads as
 * a different value. Lives here rather than in CalendarOverrides so the copy
 * tests can import it without pulling React into the node unit project.
 */
export function formatMinutePadded(minute: number): string {
  const min = minute >= 1440 ? 1439 : minute;
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ap = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

/** "HH:mm" → "9:00 AM" display. */
export function formatTime(time: string): string {
  return formatMinute(timeToMinute(time));
}

/**
 * Converts editing hour windows into backend TimeSlots. The end-of-day sentinel
 * 23:59 (1439) is stored as 1440 (midnight), matching the weekly-schedule
 * convention used elsewhere.
 */
export function toTimeSlots(slots: HourSlot[]): TimeSlot[] {
  return (
    slots
      .map((s) => {
        const endMinute = timeToMinute(s.end);
        return {
          start_minute: timeToMinute(s.start),
          end_minute: endMinute === 1439 ? 1440 : endMinute,
        };
      })
      // Drop inverted/zero-length windows — the backend zod validates the minute
      // ranges but not start < end, so a degenerate slot would otherwise persist.
      .filter((s) => s.end_minute > s.start_minute)
      .sort((a, b) => a.start_minute - b.start_minute)
  );
}

const ALL_DAY: HourSlot[] = [{ start: "00:00", end: "23:59" }];

/** A persisted TimeSlot → editing HourSlot (1440/EOD maps back to "23:59"). */
function slotToHour(s: TimeSlot): HourSlot {
  return {
    start: minuteToTime(s.start_minute),
    end: s.end_minute >= 1440 ? "23:59" : minuteToTime(s.end_minute),
  };
}

/**
 * Best-effort reconstruction of a guided setup from an already-persisted
 * schedule (weekly days + date overrides). Used to resume the guided flow on
 * its summary rather than the first step. Events can't be told apart from
 * one-off custom dates once saved (no event↔date link), so every open override
 * becomes a custom date; "blocked" (or empty-slot) overrides become blocked
 * dates.
 */
export function setupFromSaved(
  days: WeeklySchedule,
  overrides: { date: string; kind?: "custom" | "blocked"; slots: TimeSlot[] }[],
): AvailabilitySetupState {
  const base = createInitialSetupState();
  const openIndices: WeekdayIndex[] = [];
  const perDay: Record<number, HourSlot[]> = {};
  for (const { index, weekday } of WEEKDAYS) {
    const day = days.find((d) => d.weekday === weekday);
    if (day && day.slots.length > 0) {
      openIndices.push(index);
      perDay[index] = day.slots.map(slotToHour);
    }
  }
  const customDates: Record<string, HourSlot[]> = {};
  const blockedDates: Record<string, true> = {};
  for (const o of overrides) {
    if (o.kind === "blocked" || !o.slots || o.slots.length === 0) {
      blockedDates[o.date] = true;
    } else {
      customDates[o.date] = o.slots.map(slotToHour);
    }
  }
  const hasWeekly = openIndices.length > 0;
  return {
    ...base,
    mode: hasWeekly ? "weekly" : "away",
    days: hasWeekly ? openIndices : base.days,
    pattern: "each",
    perDay,
    customDates,
    blockedDates,
  };
}

export type CustomDateEntry = { date: string; slots: HourSlot[] };

/** Opened custom-schedule dates with their windows, sorted chronologically. */
export function effectiveCustomDates(
  state: AvailabilitySetupState,
): CustomDateEntry[] {
  return Object.entries(state.customDates)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, slots]) => ({ date, slots }));
}

/**
 * Which month an availability preview calendar should open on: the earliest
 * date that actually has hours, or undefined for "the current month".
 *
 * A weekly schedule already fills the current month, so `hasWeekly` short-
 * circuits. Without one, availability lives entirely on specific dates that can
 * sit months out, and defaulting to today shows the host an empty grid.
 *
 * Shared by the guided review step and the confirm step, which hold this data in
 * different shapes (setup state vs. saved overrides) but need identical
 * behaviour. Entries with no slots are blocks, not availability, and are
 * skipped; so are dates in `blocked`.
 *
 * Intentionally does no "is it in the past" filtering — that needs the client's
 * today, and calling `new Date()` on a render path would risk an SSR/CSR
 * mismatch. The calendar clamps the result against its own client-side today.
 */
export function firstOpenDateIso(
  dated: readonly { date: string; slots: readonly unknown[] }[],
  opts: { hasWeekly: boolean; blocked?: Record<string, unknown> },
): string | undefined {
  if (opts.hasWeekly) return undefined;
  let earliest: string | undefined;
  for (const entry of dated) {
    if (entry.slots.length === 0) continue;
    if (opts.blocked?.[entry.date]) continue;
    if (earliest === undefined || entry.date < earliest) earliest = entry.date;
  }
  return earliest;
}

// ── Date helpers ─────────────────────────────────────────────────────────────

export function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function fromIso(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

// ── Timezone-aware instant → wall-clock ───────────────────────────────────────
// Event start/end are absolute instants (ISO strings). We need the calendar
// date and minute-of-day *at the listing*, because the backend persists weekly
// slots and date overrides as minutes-of-day interpreted in the listing's IANA
// timezone (see listing-availability.service.ts `getWeekdayInTimezone` /
// `currentDateInTimezone`). Reading `new Date(iso).getHours()` etc. would use
// the *viewer's* timezone instead, so a Los Angeles event set up by a host in
// New York would persist on the wrong date/hour. We mirror the backend and
// resolve the wall clock in an explicit IANA zone via Intl.DateTimeFormat.

type WallClock = { year: number; month: number; day: number; minutes: number };

// Constructing an Intl.DateTimeFormat is relatively expensive; cache per zone
// (the backend does the same with timezoneDateFormatterCache).
const zoneFormatterCache = new Map<string, Intl.DateTimeFormat>();
function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = zoneFormatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    zoneFormatterCache.set(timeZone, f);
  }
  return f;
}

/** Wall-clock fields of an instant, resolved in the given IANA zone (DST-correct). */
function wallClockInZone(iso: string, timeZone: string): WallClock {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(iso));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  // Some engines render midnight as hour "24" under hour12:false — normalize.
  const hour = get("hour") % 24;
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    minutes: hour * 60 + get("minute"),
  };
}

/**
 * Fallback when no explicit zone is supplied: read the wall clock straight from
 * the ISO string's *own* UTC offset. For a venue-local ISO like
 * "2025-10-03T15:30:00-07:00" this yields 2025-10-03 15:30 regardless of where
 * the code runs — still viewer-independent, unlike the old local-getter path.
 * A bare/Z timestamp resolves to its UTC wall clock.
 */
function wallClockFromIso(iso: string): WallClock {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (m) {
    return {
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      minutes: Number(m[4]) * 60 + Number(m[5]),
    };
  }
  const d = new Date(iso);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    minutes: d.getUTCHours() * 60 + d.getUTCMinutes(),
  };
}

function wallClock(iso: string, timeZone?: string | null): WallClock {
  return timeZone ? wallClockInZone(iso, timeZone) : wallClockFromIso(iso);
}

// ── Event windows (buffer-based) ──────────────────────────────────────────────

/**
 * Calendar date (YYYY-MM-DD) an instant falls on, at the listing.
 * Pass the listing's IANA `timeZone`; omit only when it's genuinely unknown, in
 * which case the ISO string's own offset is used (never the viewer's timezone).
 */
export function localDateIso(iso: string, timeZone?: string | null): string {
  const w = wallClock(iso, timeZone);
  return isoDate(w.year, w.month - 1, w.day);
}

/** Minutes since midnight for an instant, at the listing (see localDateIso). */
export function minutesOfDay(iso: string, timeZone?: string | null): number {
  return wallClock(iso, timeZone).minutes;
}

/**
 * Today's calendar date (YYYY-MM-DD) at the listing. Mirrors the backend's
 * `currentDateInTimezone`, so date windows are anchored to the listing's day —
 * not the viewer's — and a remote host can't shift the range by a date.
 */
export function localToday(timeZone?: string | null): string {
  return localDateIso(new Date().toISOString(), timeZone);
}

// ── Naming the listing's frame in the UI ──────────────────────────────────────
//
// Everything above resolves availability in the listing's timezone. That's
// correct but invisible: a host in Los Angeles editing a Michigan spot sees the
// day's hours start at 12:00 PM with nothing on screen explaining that it's
// already 12:30 PM there. These helpers exist so the UI can say whose clock it
// means, in the host's own words.

/**
 * The IANA zone the code is running in — the viewer's, in the browser.
 *
 * For the case where a listing's own zone is unknown but the arithmetic above
 * still has to happen in *some* named frame. Passing `undefined` through instead
 * is not equivalent: `localDateIso`/`minutesOfDay` then read the ISO string's
 * own offset (UTC for the backend's `Z` stamps) and `listingWallClockToIso`
 * writes the wall clock as UTC, so a screen that also renders viewer-local
 * labels ends up doing its maths in a frame none of its text is in. Hand this in
 * to keep both on one clock.
 *
 * Only ever a fallback: when the listing's zone is known, pass that. And it
 * names the *viewer's* clock, not the listing's, so it must not reach a zone
 * label — the UI would be asserting a zone it doesn't actually know.
 */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Short zone label for an instant at `timeZone` — "EST", "EDT", "GMT+8".
 * Derived from the instant rather than stored, so it's DST-correct: Detroit is
 * "EDT" in July and "EST" in January. Empty string when the zone is unknown, so
 * callers can omit the label rather than print a wrong one.
 */
export function zoneAbbrev(timeZone?: string | null, iso?: string): string {
  return zoneNamePart(timeZone, "short", iso);
}

/**
 * Full zone name for an instant at `timeZone` — "Eastern Daylight Time".
 * For prose where an abbreviation would be too terse. Empty when unknown.
 */
export function zoneLongName(timeZone?: string | null, iso?: string): string {
  return zoneNamePart(timeZone, "long", iso);
}

// Formatters are cached per (zone, style) for the same reason zoneFormatter is:
// callers read these during render, so construction cost would land on every
// keystroke in the slot editor.
const zoneNameCache = new Map<string, Intl.DateTimeFormat>();
function zoneNamePart(
  timeZone: string | null | undefined,
  style: "short" | "long",
  iso?: string,
): string {
  if (!timeZone) return "";
  const key = `${style}:${timeZone}`;
  let f = zoneNameCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: style });
    zoneNameCache.set(key, f);
  }
  const parts = f.formatToParts(iso ? new Date(iso) : new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

/**
 * Minutes the listing's clock reads ahead of the viewer's (negative when
 * behind). 0 means the two clocks agree and the UI has nothing to explain.
 *
 * Compares UTC offsets, not wall-clock readings. Subtracting wall clocks looks
 * equivalent and isn't: the two can sit on different calendar days, and the
 * result would need wrapping into a 24h range that real zones overflow — offsets
 * span UTC-12 to UTC+14, so a genuine gap reaches 26 hours and any ±12h wrap
 * silently flips its sign (Detroit vs Tokyo is 13 hours *behind*, which wraps to
 * "11 ahead"). Offsets subtract cleanly at any distance.
 *
 * It's still a clock comparison rather than a zone-id one: a host in Phoenix
 * editing a Los Angeles listing in July shares an offset with it despite the
 * different ids, and shouldn't be told about a difference they can't observe.
 * Being offset-based, it's also DST-correct on both sides.
 *
 * `viewerTimeZone` overrides the ambient browser zone (for tests).
 */
export function viewerClockOffsetMinutes(
  timeZone?: string | null,
  viewerTimeZone?: string | null,
): number {
  if (!timeZone) return 0;
  const now = new Date();
  const listing = zoneOffsetMs(now, timeZone) / 60_000;
  const viewer = viewerTimeZone
    ? zoneOffsetMs(now, viewerTimeZone) / 60_000
    : -now.getTimezoneOffset();
  return Math.round(listing - viewer);
}

/**
 * "3 hours ahead of you" / "90 minutes behind you" — the clock gap in the
 * second person, for appending to a sentence about the listing's time. Empty
 * when the clocks agree, so it drops out of the sentence entirely.
 */
export function formatClockOffset(offsetMinutes: number): string {
  if (offsetMinutes === 0) return "";
  const dir = offsetMinutes > 0 ? "ahead of" : "behind";
  const mins = Math.abs(offsetMinutes);
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  if (rem > 0) parts.push(`${rem} minute${rem === 1 ? "" : "s"}`);
  return `${parts.join(" ")} ${dir} you`;
}

/**
 * Shift a YYYY-MM-DD calendar date by whole years and/or days. Pure calendar
 * math done in UTC, so it's independent of the viewer's timezone and never
 * drifts across a DST boundary.
 */
export function shiftIsoDate(
  dateIso: string,
  { years = 0, days = 0 }: { years?: number; days?: number },
): string {
  const [y, m, d] = dateIso.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  if (years) dt.setUTCFullYear(dt.getUTCFullYear() + years);
  if (days) dt.setUTCDate(dt.getUTCDate() + days);
  return isoDate(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
}

/** Offset (ms) of `timeZone` at instant `date`: its wall clock minus UTC. */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const hour = get("hour") % 24;
  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second"),
  );
  return asUTC - date.getTime();
}

/** Wide enough to bracket any single DST transition, narrow enough to catch one. */
const OFFSET_PROBE_MS = 86_400_000;

/**
 * The absolute instant (ISO) for a wall-clock time on a listing-local calendar
 * date — the inverse of {@link localDateIso}/{@link minutesOfDay}. Use it to
 * turn a listing-local day boundary (e.g. 00:00:00.000 / 23:59:59.999) into an
 * exact query bound, so the window covers the listing's day regardless of where
 * the viewer is. With no zone the wall clock is interpreted as UTC (matching the
 * read helpers' fallback).
 *
 * A local calendar is not a total order over instants twice a year, so the two
 * DST edge cases each get an explicit policy — see {@link wallClockExistsInZone}
 * for the check that lets a picker avoid the first one entirely:
 *
 * - **Spring forward (the wall clock does not exist).** 02:30 on 2026-03-08 in
 *   New York is skipped: the clock jumps 02:00 EST → 03:00 EDT. The time is
 *   shifted *forward* by the gap, so 02:30 resolves to 03:30 EDT. Forward and
 *   not backward because the result is a booking bound: an instant later than
 *   the one named costs a caller minutes of parking, while an earlier one hands
 *   them a window that started before the one they were shown. The earlier
 *   reading is also indistinguishable from a legitimately picked slot — an
 *   earlier cut resolved 02:30 to 01:30 EST, which round-trips as an hour the
 *   picker offers on its own, so nothing downstream could tell the two apart.
 * - **Fall back (the wall clock happens twice).** 01:30 on 2026-11-01 in New
 *   York occurs at 05:30Z (EDT) and again at 06:30Z (EST). The **first** —
 *   earliest — occurrence wins. Same reason: it is the reading that cannot
 *   silently swallow the start of a window, and for an end bound it is the
 *   conservative one. Either candidate round-trips through
 *   `localDateIso`/`minutesOfDay`, so this is a choice, not a correctness fix;
 *   it is pinned by test so it stays one policy rather than an accident of
 *   whichever offset the resolver happened to probe first.
 */
export function listingWallClockToIso(
  dateIso: string,
  {
    hour = 0,
    minute = 0,
    second = 0,
    ms = 0,
  }: { hour?: number; minute?: number; second?: number; ms?: number } = {},
  timeZone?: string | null,
): string {
  const [y, mo, d] = dateIso.split("-").map(Number);
  // The desired wall clock, provisionally read as if it were UTC.
  const guessUTC = Date.UTC(
    y ?? 1970,
    (mo ?? 1) - 1,
    d ?? 1,
    hour,
    minute,
    second,
    ms,
  );
  if (!timeZone) return new Date(guessUTC).toISOString();

  // utc = wallClock − offset, but which offset is the whole question: the
  // instant that answers it is the one being solved for. So both offsets in
  // force around the target are probed a day out on either side — far enough
  // that a transition between them is bracketed rather than straddled — and each
  // is turned into a candidate instant. On an ordinary day they agree and there
  // is one candidate. Iterating from a single probe instead (offset at the
  // guess, then re-check) is what produced the backward spring-forward shift:
  // the second pass has no way to tell "the offset changed, so correct it" from
  // "this wall clock has no offset at all".
  const offBefore = zoneOffsetMs(
    new Date(guessUTC - OFFSET_PROBE_MS),
    timeZone,
  );
  const offAfter = zoneOffsetMs(new Date(guessUTC + OFFSET_PROBE_MS), timeZone);
  const candidates = [guessUTC - offBefore, guessUTC - offAfter];
  // A candidate is real only if the zone actually reads back the wall clock that
  // produced it — the round trip the two policies above are decided on.
  const real = candidates.filter(
    (c) => zoneOffsetMs(new Date(c), timeZone) === guessUTC - c,
  );
  // Real and ambiguous → earliest. Unreal (a gap) → the forward-shifted one,
  // which is the later candidate: the two differ by exactly the gap.
  const instant = real.length > 0 ? Math.min(...real) : Math.max(...candidates);
  return new Date(instant).toISOString();
}

/**
 * Whether `timeZone`'s clock ever reads this wall clock on this calendar date.
 *
 * False only inside a spring-forward gap — 02:00–02:59 on 2026-03-08 in New
 * York, where the clock goes straight from 02:00 to 03:00. Pickers built from a
 * fixed slot table offer those times anyway, because the table knows nothing
 * about the destination; filter with this so a driver is never shown a time that
 * cannot be arrived at. Repeated fall-back times return true — they exist twice,
 * and {@link listingWallClockToIso} documents which one is taken.
 *
 * With no zone every wall clock exists: the no-zone path reads the clock as UTC,
 * which has no transitions.
 */
export function wallClockExistsInZone(
  dateIso: string,
  wallClock: { hour?: number; minute?: number } = {},
  timeZone?: string | null,
): boolean {
  if (!timeZone) return true;
  const { hour = 0, minute = 0 } = wallClock;
  const iso = listingWallClockToIso(dateIso, { hour, minute }, timeZone);
  return (
    localDateIso(iso, timeZone) === dateIso &&
    minutesOfDay(iso, timeZone) === hour * 60 + minute
  );
}

function clampMin(n: number): number {
  return Math.max(0, Math.min(1440, n));
}

function addDaysIso(dateIso: string, n: number): string {
  const d = fromIso(dateIso);
  d.setDate(d.getDate() + n);
  return isoDate(d.getFullYear(), d.getMonth(), d.getDate());
}

/** minutes → "HH:mm", mapping 1440 → 23:59 so it round-trips through toTimeSlots. */
function minuteToEditTime(minute: number): string {
  return minute >= 1440 ? "23:59" : minuteToTime(minute);
}

/** Merges overlapping/adjacent windows into a sorted, non-overlapping list. */
function mergeHourSlots(slots: HourSlot[]): HourSlot[] {
  const ranges = slots
    .map((s) => ({
      start: timeToMinute(s.start),
      end: s.end === "23:59" ? 1440 : timeToMinute(s.end),
    }))
    .filter((r) => r.end > r.start)
    .sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) last.end = Math.max(last.end, r.end);
    else merged.push({ ...r });
  }
  return merged.map((r) => ({
    start: minuteToEditTime(r.start),
    end: minuteToEditTime(r.end),
  }));
}

/** Human "Sat, Oct 3 · 3:00 – 11:00 PM" (or a date span for multi-day events). */
export function formatEventWhen(
  ev: SelectedEvent,
  timeZone?: string | null,
): string {
  const startDate = localDateIso(ev.startsAt, timeZone);
  const endDate = localDateIso(ev.endsAt, timeZone);
  const dayLabel = (iso: string) =>
    fromIso(iso).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  if (startDate === endDate) {
    return `${dayLabel(startDate)} · ${formatMinute(minutesOfDay(ev.startsAt, timeZone))} – ${formatMinute(minutesOfDay(ev.endsAt, timeZone))}`;
  }
  return `${dayLabel(startDate)} → ${dayLabel(endDate)}`;
}

/**
 * Buffer-computed open windows per date, derived from each selected event's
 * real start/end times. Single-day events get one window
 * [start − before, end + after] (clamped to the day). Multi-day events open
 * from the buffered start through end-of-day, all-day through the middle, and
 * until the buffered end on the final day. Windows from multiple events on the
 * same day are merged.
 */
export function computedEventDateSlots(
  state: AvailabilitySetupState,
  timeZone?: string | null,
): Map<string, HourSlot[]> {
  return eventDateSlotsFor(
    state.selectedEvents,
    state.bufferBeforeMin,
    state.bufferAfterMin,
    timeZone,
  );
}

/**
 * The same computation as {@link computedEventDateSlots}, over an explicit list
 * of events rather than the whole setup state.
 *
 * Split out so a caller can ask what *one* event day would open on its own —
 * which is what {@link effectiveEventDatesFor} layers manual per-date edits onto
 * to decide "you're already open for this one" per row on the select step.
 */
export function eventDateSlotsFor(
  events: SelectedEvent[],
  bufferBeforeMin: number,
  bufferAfterMin: number,
  timeZone?: string | null,
): Map<string, HourSlot[]> {
  const raw = new Map<string, HourSlot[]>();
  const push = (date: string, start: number, end: number) => {
    if (end <= start) return;
    const list = raw.get(date) ?? [];
    list.push({ start: minuteToEditTime(start), end: minuteToEditTime(end) });
    raw.set(date, list);
  };
  for (const ev of events) {
    const startDate = localDateIso(ev.startsAt, timeZone);
    const endDate = localDateIso(ev.endsAt, timeZone);
    const startMin = clampMin(
      minutesOfDay(ev.startsAt, timeZone) - bufferBeforeMin,
    );
    const endMin = clampMin(minutesOfDay(ev.endsAt, timeZone) + bufferAfterMin);
    if (startDate === endDate) {
      push(startDate, startMin, endMin === startMin ? 1440 : endMin);
      continue;
    }
    push(startDate, startMin, 1440);
    let cursor = addDaysIso(startDate, 1);
    while (cursor < endDate) {
      push(cursor, 0, 1440);
      cursor = addDaysIso(cursor, 1);
    }
    // An event ending at 00:00 on the final day (with no after-buffer) ends at
    // that day's very start — it occupies none of it, so push a zero-length
    // window that `push` drops rather than opening the whole day all-day.
    push(endDate, 0, endMin);
  }
  const out = new Map<string, HourSlot[]>();
  for (const [date, slots] of raw) out.set(date, mergeHourSlots(slots));
  return out;
}

export type EventDateEntry = {
  date: string;
  slots: HourSlot[];
  computed: HourSlot[];
  manual: boolean;
};

/** Effective event windows per date — manual per-date edits win over the buffer. */
export function effectiveEventDates(
  state: AvailabilitySetupState,
  timeZone?: string | null,
): EventDateEntry[] {
  return effectiveEventDatesFor(state.selectedEvents, state, timeZone);
}

/**
 * The same computation as {@link effectiveEventDates}, over an explicit list of
 * events rather than everything the host has selected.
 *
 * Split out so a caller can ask what *one* event day would write — which is how
 * "you're already open for this one" is decided per row on the select step,
 * before the host has selected anything. Manual edits are keyed by date, not by
 * event, so they apply here too: a row landing on a date the host has already
 * edited by hand would write those hours, and measuring the buffer window
 * instead would have that row disagree with the review step about the same day.
 */
export function effectiveEventDatesFor(
  events: SelectedEvent[],
  state: AvailabilitySetupState,
  timeZone?: string | null,
): EventDateEntry[] {
  const computed = eventDateSlotsFor(
    events,
    state.bufferBeforeMin,
    state.bufferAfterMin,
    timeZone,
  );
  return [...computed.keys()].sort().map((date) => {
    const manualSlots = state.eventDateSlots[date];
    const base = computed.get(date) ?? [];
    return {
      date,
      computed: base,
      slots: manualSlots ?? base,
      manual: !!manualSlots,
    };
  });
}

// ── Translation: setup → backend model ────────────────────────────────────────

export type SetupOverride = {
  date: string;
  kind: "custom" | "blocked";
  slots: TimeSlot[];
};

export type TranslatedAvailability = {
  /** Recurring weekly schedule — always all 7 days (closed days have no slots). */
  days: WeeklySchedule;
  /** Single-date openings that aren't part of the recurring baseline. */
  overrides: SetupOverride[];
};

function weeklySlotsForDay(
  state: AvailabilitySetupState,
  index: WeekdayIndex,
): HourSlot[] {
  if (state.pattern === "same")
    return state.sameAllDay ? ALL_DAY.map((s) => ({ ...s })) : state.sameHrs;
  if (state.pattern === "weekend")
    return WEEKEND_INDICES.includes(index)
      ? state.weekendHrs
      : state.weekdayHrs;
  return state.perDay[index] ?? [{ start: "09:00", end: "17:00" }];
}

/**
 * What a date would be open for under the setup being built, *before* any event
 * window is laid over it — the baseline an event override would replace.
 *
 * This is the honest comparison for the flows that replace a listing's whole
 * schedule (the new-listing wizard, "Set up availability"): the host's saved
 * hours are on their way out, and what an event date is measured against is the
 * weekly schedule they just chose in the same wizard. Comparing against the
 * server instead would announce a narrowing on every date for a spot currently
 * open 24/7 — including dates the host has since closed — which is noise, not a
 * warning. The additive "Add events" flow leaves the weekly schedule alone, so
 * it passes the saved schedule in explicitly rather than using this.
 *
 * Resolution order matches {@link translateSetup}, minus the event layer:
 * blocked closes the date, an explicit one-off date wins over the recurring
 * baseline, and the weekly schedule applies only in the modes that have one.
 * Returns `[]` for "open for nothing", never null — this is a complete answer
 * about a schedule wholly in hand, not a snapshot that might not have loaded.
 */
export function draftSlotsForDate(
  state: AvailabilitySetupState,
  date: string,
): TimeSlot[] {
  const hasWeekly = state.mode === "weekly" || state.mode === "both";
  // Blocks only carry meaning against a recurring baseline (see translateSetup);
  // without one the date is closed anyway, so both paths answer the same [].
  if (hasWeekly && state.blockedDates[date]) return [];
  const custom = state.customDates[date];
  if (custom) return toTimeSlots(custom);
  if (!hasWeekly) return [];
  const index = fromIso(date).getDay() as WeekdayIndex;
  if (!state.days.includes(index)) return [];
  return toTimeSlots(weeklySlotsForDay(state, index));
}

/**
 * Translates the guided setup into the two primitives the backend understands:
 * a recurring weekly schedule and a set of single-date overrides. Per product
 * rule, anything recurring lands in the weekly schedule; anything tied to
 * specific dates (events, trip openings) becomes a custom date override.
 */
export function translateSetup(
  state: AvailabilitySetupState,
  timeZone?: string | null,
): TranslatedAvailability {
  const hasWeekly = state.mode === "weekly" || state.mode === "both";
  const hasEvents = state.mode === "events" || state.mode === "both";

  const days: WeeklySchedule = WEEKDAYS.map(({ index, weekday }) => {
    const open = hasWeekly && state.days.includes(index);
    return {
      weekday,
      slots: open ? toTimeSlots(weeklySlotsForDay(state, index)) : [],
    };
  });

  const overrideMap = new Map<string, SetupOverride>();
  if (hasEvents) {
    for (const entry of effectiveEventDates(state, timeZone)) {
      overrideMap.set(entry.date, {
        date: entry.date,
        kind: "custom",
        slots: toTimeSlots(entry.slots),
      });
    }
  }
  // The "custom" one-off-dates step is the final step in every guided flow, so
  // single-date openings can be added regardless of mode. They win over an
  // event on the same date.
  for (const { date, slots } of effectiveCustomDates(state)) {
    overrideMap.set(date, { date, kind: "custom", slots: toTimeSlots(slots) });
  }
  // Blocked dates close a single day (e.g. a weekly-open day the host is away
  // for) and only carry meaning against a recurring baseline. Without a weekly
  // schedule the day is already closed, so skip the redundant override — this
  // also prevents stale blocks from a prior mode leaking into an away/events
  // setup where they'd be serialized as pointless closed-on-closed overrides.
  if (hasWeekly) {
    for (const date of Object.keys(state.blockedDates)) {
      overrideMap.set(date, { date, kind: "blocked", slots: [] });
    }
  }

  const overrides: SetupOverride[] = [...overrideMap.values()].sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  return { days, overrides };
}
